import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {environment} from "../../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private readonly apiUrl = environment.venway3ApiHost;

  constructor(private http: HttpClient) { }

  /**
   * Get resource-index by type with optional parameters
   * @param modelType The model type to fetch
   * @param parameters Query parameters
   */
  get(modelType: string, parameters: any = {}): Observable<any> {
    let params = new HttpParams();

    // Convert the parameters object to HttpParams
    Object.keys(parameters).forEach(key => {
      params = params.set(key, parameters[key]);
    });

    return this.http.get(this.apiUrl + `/api/${modelType}`, { params }).pipe(
      map((response: any) => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Get count of resource-index by type
   * @param modelType The model type to count
   * @param parameters Query parameters
   */
  getCount(modelType: string, parameters: any = {}): Observable<number> {
    let params = new HttpParams();

    // Convert the parameters object to HttpParams
    Object.keys(parameters).forEach(key => {
      params = params.set(key, parameters[key]);
    });

    return this.http.get(this.apiUrl + `/api/${modelType}/count`, { params }).pipe(
      map((response: any) => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Create a new resource
   * @param modelType The type of model to create
   * @param model The model data
   */
  create(modelType: string, model: any): Observable<any> {
    return this.http.post(this.apiUrl + `/api/${modelType}`, model).pipe(
      map((response: any) => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Update an existing resource
   * @param modelType The type of model to update
   * @param model The model data
   */
  update(modelType: string, model: any): Observable<any> {
    return this.http.put(this.apiUrl + `/api/${modelType}`, model).pipe(
      map((response: any) => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Delete a resource
   * @param modelType The type of model to delete
   * @param modelId The ID of the resource to delete
   */
  delete(modelType: string, modelId: string): Observable<any> {
    let params = new HttpParams().set('id', modelId);

    return this.http.delete(this.apiUrl + `/api/${modelType}`, { params }).pipe(
      map((response: any) => response.data),
      catchError(this.handleError)
    );
  }

  /**
   * Paginate a collection of resource-index
   * @param modelType The type of model to paginate
   * @param depth The depth of related resource-index to include
   * @param tableState The current table state (sorting, filtering, pagination)
   * @param columns Optional columns to include
   */
  paginate(modelType: string, depth: number, tableState: any, columns?: string[]): Observable<any> {
    // Handle search parameters
    const search = tableState.search.predicateObject || {};

    // Handle sort parameters
    const sort: any = {};
    if (tableState.sort.predicate) {
      sort.sort = tableState.sort.predicate;
      sort.reverse = Number(tableState.sort.reverse);
    }

    // Handle column parameters
    const column: any = {};
    if (columns && columns.length) {
      column.columns = columns.join(',');
    }

    // Update total count for pagination
    this.getCount(modelType, search).subscribe(count => {
      tableState.pagination.totalItemCount = count;
      tableState.pagination.numberOfPages = Math.ceil(count / tableState.pagination.number);
    });

    // Build parameters for the query
    const params = {
      depth: depth,
      limit: tableState.pagination.number,
      offset: tableState.pagination.start,
      ...search,
      ...sort,
      ...column
    };

    // Fetch the data
    return this.get(modelType, params);
  }

  /**
   * Handle HTTP errors
   * @param error The error object
   */
  private handleError(error: any): Observable<never> {
    console.error('API Error:', error);

    let errorMessage = 'An unknown error occurred';
    if (error.error && error.error.message) {
      errorMessage = error.error.message;
    } else if (error.message) {
      errorMessage = error.message;
    }

    return throwError(errorMessage);
  }
}
